/**
 * Filter DSL Parser/Encoder
 *
 * Parses and encodes Filter DSL for URL-based filtering
 * Example: "country:US,device:mobile,date>2025-01-01"
 */

import type { FilterClause, FilterOperator } from "./types";

/**
 * Parse Filter DSL string into FilterClause array
 * Example: "country:US,device:mobile,date>2025-01-01"
 */
export function parseFilterDSL(dsl: string): FilterClause[] {
  if (!dsl || dsl.trim() === "") return [];

  const clauses: FilterClause[] = [];
  const parts = dsl.split(",");

  for (const part of parts) {
    const match = part.match(/^([a-z_]+)(:|>=|<=|!=|>|<)(.+)$/);
    if (!match) continue;

    const [, field, operator, value] = match;
    clauses.push({
      field,
      operator: operator as FilterOperator,
      value: decodeURIComponent(value),
    });
  }

  return clauses;
}

/**
 * Encode FilterClause array into DSL string
 */
export function encodeFilterDSL(filters: FilterClause[]): string {
  if (filters.length === 0) return "";

  return filters
    .map((f) => `${f.field}${f.operator}${encodeURIComponent(f.value)}`)
    .join(",");
}

/**
 * Add filter clause (replaces if same field exists)
 */
export function addFilter(
  current: FilterClause[],
  field: string,
  operator: FilterOperator,
  value: string
): FilterClause[] {
  const filtered = current.filter((f) => f.field !== field);
  return [...filtered, { field, operator, value }];
}

/**
 * Remove filter by field
 */
export function removeFilter(
  current: FilterClause[],
  field: string
): FilterClause[] {
  return current.filter((f) => f.field !== field);
}

/**
 * Convert filters to API format (backend expects different shape)
 */
function filtersToAPIParams(
  filters: FilterClause[]
): Record<string, string> {
  const params: Record<string, string> = {};

  for (const filter of filters) {
    // Map frontend field names to backend field names if different
    const apiField = fieldMapping[filter.field] || filter.field;

    // For simple equality, just use the value
    if (filter.operator === ":") {
      params[apiField] = filter.value;
    } else {
      // For comparisons, include operator
      params[apiField] = `${filter.operator}${filter.value}`;
    }
  }

  return params;
}

// Map frontend field names to backend field names
const fieldMapping: Record<string, string> = {
  country: "location",
  device: "deviceType",
  // Add more mappings as needed
};
